import { AdobeAnalyticsUserRole } from '@/hooks/AdobeAnalyticsHooks/AdobeAnalyticsUserRole';
import { IdToken, USER_ACCESS_GROUPS, USER_ACCESS_LEVEL, UserAccessLevel, UserInfo } from '@/types/auth';
import jwt, { JwtPayload } from 'jwt-decode';

const FIVE_MIN_IN_MS = 1000 * 60 * 5;
export const USER_HAVE_NO_ACCESS_TO_ANY_RESOURCE = 'USER_HAVE_NO_ACCESS_TO_ANY_RESOURCE';

export const hasTokenExpired = (currentAccessToken: string): boolean => {
  const decodedCurrentAccessToken = jwt<JwtPayload>(currentAccessToken);
  const expirationDate = new Date(0);
  expirationDate.setUTCSeconds(decodedCurrentAccessToken.exp);
  const now = new Date();
  return expirationDate.getTime() - now.getTime() < FIVE_MIN_IN_MS;
};

const getCountriesByLocale = (locale: string): string[] => {
  const hasAllAccess = /CESAPP_ALL|CES_ADMINS/.test(locale);
  if (hasAllAccess) return [];
  const matches = locale.match(/CESAPP_\w{2}/g);
  return matches.map(countryGroup => {
    return countryGroup.match(/_(\w{2})/)[1];
  });
};

export const checkUserAccess = (idToken: string) => {
  const decoded = jwt<IdToken>(idToken);
  if (!decoded.locale || !/CESAPP_\w+|CES_ADMINS/.test(decoded.locale)) {
    throw new Error(USER_HAVE_NO_ACCESS_TO_ANY_RESOURCE);
  }
};

interface GetAdobeAnalyticsUserRoleProps {
  isAdmin: boolean;
  isGlobalUser: boolean;
}

const isAdminUser = (accessLevel: UserAccessLevel): boolean => accessLevel === USER_ACCESS_LEVEL.ADMIN;

const getAdobeAnalyticsUserRole = ({ isAdmin, isGlobalUser }: GetAdobeAnalyticsUserRoleProps) => {
  if (isAdmin) return AdobeAnalyticsUserRole.DEVELOPER;
  if (isGlobalUser) return AdobeAnalyticsUserRole.GLOBAL_USER;
  return AdobeAnalyticsUserRole.LOCAL_USER;
};

const getUserAccessLevel = (groups: string[]): UserAccessLevel => {
  if (groups.includes(USER_ACCESS_GROUPS[USER_ACCESS_LEVEL.ADMIN])) {
    return USER_ACCESS_LEVEL.ADMIN;
  }
  if (groups.includes(USER_ACCESS_GROUPS[USER_ACCESS_LEVEL.GLOBAL_USER])) {
    return USER_ACCESS_LEVEL.GLOBAL_USER;
  }
  return USER_ACCESS_LEVEL.LOCAL_USER;
};

const normalizeOverrideCountries = (overrideCountries?: string[]): string[] => {
  if (!overrideCountries?.length) return [];
  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const raw of overrideCountries) {
    const code = raw.trim().toUpperCase();
    if (code && !seen.has(code)) {
      seen.add(code);
      normalized.push(code);
    }
  }
  return normalized;
};

export const getUserInfo = (idToken: string, overrideCountries?: string[]): UserInfo => {
  const decoded = jwt<IdToken>(idToken);
  if (decoded.token_use !== 'id') {
    throw new Error('ID token expected');
  }

  const userGroupsArray = decoded.locale.replace(/[\[\] ]+/g, '').split(',');
  const rawAccessLevel = getUserAccessLevel(userGroupsArray);

  // Only an admin (CES_ADMINS) may simulate a country
  // Derived from the raw token so an active override, which flips accessLevel/isAdmin, can't hide the control.
  const canSimulate = rawAccessLevel === USER_ACCESS_LEVEL.ADMIN;

  const simulatedCountries = canSimulate ? normalizeOverrideCountries(overrideCountries) : [];
  const accessLevel = simulatedCountries.length ? USER_ACCESS_LEVEL.LOCAL_USER : rawAccessLevel;
  const isAdmin = isAdminUser(accessLevel);
  const countries = simulatedCountries.length ? simulatedCountries : getCountriesByLocale(decoded.locale);

  return {
    firstName: decoded.given_name,
    lastName: decoded.family_name,
    email: decoded.email,
    username: decoded.identities[0]?.userId,
    countries,
    isAdmin,
    accessLevel,
    canSimulate,
    sub: decoded.sub,
    adobeAnalyticsRole: getAdobeAnalyticsUserRole({ isAdmin, isGlobalUser: countries.length === 0 }),
  };
};
